import { formatBytes } from '../../api/client'
import type { VisualAsset } from '../../api/visuals'
import { formatDuration } from './formatDuration'

const formatMimeSubtype = (mimeType: string | null): string | null => {
  if (!mimeType) {
    return null
  }

  const subtype = mimeType.split('/')[1]?.trim().toLowerCase()

  if (!subtype) {
    return mimeType.trim().toLowerCase()
  }

  return subtype.split('+')[0] ?? subtype
}

const formatResolution = (width: number | null, height: number | null): string | null => {
  if (!width || !height) {
    return null
  }

  return `${width} x ${height}`
}

export type VisualAssetMetadataItem = {
  key: 'format' | 'resolution' | 'size' | 'duration'
  label: string
  value: string
}

export const getVisualAssetMetadataItems = (asset: VisualAsset): VisualAssetMetadataItem[] => {
  const items: VisualAssetMetadataItem[] = [
    {
      key: 'format',
      label: 'Format',
      value: formatMimeSubtype(asset.mime_type) ?? 'unknown format'
    }
  ]
  const resolution = formatResolution(asset.width, asset.height)
  const duration = asset.asset_type === 'video'
    ? formatDuration(asset.duration_seconds)
    : null

  if (resolution) {
    items.push({
      key: 'resolution',
      label: 'Resolution',
      value: resolution
    })
  }

  items.push({
    key: 'size',
    label: 'Size',
    value: formatBytes(asset.size_bytes)
  })

  if (duration) {
    items.push({
      key: 'duration',
      label: 'Duration',
      value: duration
    })
  }

  return items
}

export const formatVisualAssetMetadata = (asset: VisualAsset): string => {
  return getVisualAssetMetadataItems(asset)
    .filter((item) => item.key !== 'duration')
    .map((item) => item.value)
    .join(' · ')
}
